import type {
  AppNotification,
  Blueprint,
  CanvasOverview,
  CanvasProcess,
  CanvasSchema,
  BloxGuide,
  Catalog,
  Contract,
  DemoAccount,
  DemoUsersResult,
  Folder,
  FormsMeta,
  FormType,
  GuideIndexItem,
  LogEntry,
  LogKind,
  OpenAction,
  MyAction,
  MyUsage,
  PlatformUsers,
  QueryResult,
  Readiness,
  ReleaseLog,
  Phase,
  PhasePatch,
  Project,
  ProjectDetail,
  ProjectForm,
  ProjectFormDetail,
  ProjectGroup,
  ProjectMember,
  ProjectSummary,
  RegisterItem,
  RegisterItemDetail,
  RegisterKind,
  SitePhoto,
  Transmittal,
  Stakeholder,
  TenantConfig,
  TenantPublic,
  TenantRecord,
  TenantSummary,
  User,
  UserListItem,
  UserSettings,
  VaultFile,
  ViewAsTarget,
} from "./types";

interface ProjectInput {
  name: string;
  code?: string;
  clientName?: string;
  location?: string;
  sector?: string;
  status?: string;
  startDate?: string;
  targetDate?: string;
  budget?: number | null;
  description?: string;
}

const BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";
/** Health sits outside the versioned API — it is about the deployment, not the data. */
export const HEALTH_URL = BASE.replace(/\/v1\/?$/, "") + "/health";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(BASE + path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers ?? {}) },
    ...opts,
  });
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    let detail = res.statusText;
    if (data && typeof data === "object" && "detail" in data) {
      detail = String((data as { detail: unknown }).detail);
    }
    throw new ApiError(res.status, detail, data);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => req<T>(p),
  post: <T>(p: string, body?: unknown) => req<T>(p, { method: "POST", body: JSON.stringify(body ?? {}) }),
  put: <T>(p: string, body?: unknown) => req<T>(p, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  patch: <T>(p: string, body?: unknown) => req<T>(p, { method: "PATCH", body: JSON.stringify(body ?? {}) }),
  del: <T>(p: string) => req<T>(p, { method: "DELETE" }),
};

/** Typed endpoints used by the shell + core modules. */
export const endpoints = {
  resolveTenant: () =>
    api.get<TenantConfig>(`/tenant/resolve?host=${encodeURIComponent(location.host)}`),
  myTenant: () => api.get<TenantConfig>("/tenant/mine"),
  publicTenant: (slug: string) => api.get<TenantPublic>(`/tenant/public/${slug}`),

  login: (email: string, password: string, remember = false) =>
    api.post<User>("/auth/login", { email, password, remember }),
  me: () => api.get<User>("/auth/me"),
  logout: () => api.post<{ ok: boolean }>("/auth/logout"),
  forgotPassword: (email: string) =>
    api.post<{ ok: boolean; resetToken?: string; resetUrl?: string }>("/auth/forgot", { email }),
  resetPassword: (token: string, newPassword: string) =>
    api.post<User>("/auth/reset", { token, newPassword }),

  getSettings: () => api.get<UserSettings>("/me/settings"),
  saveSettings: (s: Partial<UserSettings>) => api.put<UserSettings>("/me/settings", s),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: boolean }>("/me/password", { currentPassword, newPassword }),
  updateProfile: (body: { name?: string; avatarUrl?: string }) =>
    api.put<User>("/me/profile", body),

  listNotifications: () => api.get<AppNotification[]>("/me/notifications"),
  unreadCount: () => api.get<{ count: number }>("/me/notifications/unread-count"),
  markNotificationRead: (id: string) =>
    api.post<{ count: number }>(`/me/notifications/${id}/read`),
  markAllNotificationsRead: () => api.post<{ count: number }>("/me/notifications/read-all"),

  getCatalog: () => api.get<Catalog>("/catalog"),

  getMyUsage: () => api.get<MyUsage>("/me/usage"),

  // records (tenant data DB, gated by the crm module)
  listRecords: () => api.get<TenantRecord[]>("/records"),
  createRecord: (body: { title: string; kind?: string; body?: string }) =>
    api.post<TenantRecord>("/records", body),
  deleteRecord: (id: string) => api.del<{ ok: boolean }>(`/records/${id}`),

  // engineering projects (tenant data DB, gated by eng.projects)
  listProjects: () => api.get<Project[]>("/projects"),
  createProject: (body: ProjectInput) => api.post<ProjectDetail>("/projects", body),
  getProject: (id: string) => api.get<ProjectDetail>(`/projects/${id}`),
  updateProject: (id: string, body: Partial<ProjectInput>) =>
    api.put<ProjectDetail>(`/projects/${id}`, body),
  addStakeholder: (
    pid: string,
    body: { stype: string; name: string; org?: string; email?: string },
  ) => api.post<Stakeholder>(`/projects/${pid}/stakeholders`, body),
  removeStakeholder: (pid: string, sid: string) =>
    api.del<{ ok: boolean }>(`/projects/${pid}/stakeholders/${sid}`),
  updatePhase: (pid: string, phid: string, body: PhasePatch) =>
    api.patch<Phase>(`/projects/${pid}/phases/${phid}`, body),

  // the supervision package: contract terms, the money picture, site photos
  getContract: (pid: string) => api.get<Contract>(`/projects/${pid}/contract`),
  saveContract: (pid: string, body: Partial<Contract>) =>
    api.put<Contract>(`/projects/${pid}/contract`, body),
  projectSummary: (pid: string) => api.get<ProjectSummary>(`/projects/${pid}/summary`),
  setBaseline: (pid: string, reason: string) =>
    api.post<Phase[]>(`/projects/${pid}/baseline`, { reason }),
  listPhotos: (pid: string) => api.get<SitePhoto[]>(`/projects/${pid}/photos`),

  // project registers — controlled lists that live for the whole project
  registersCatalog: () => api.get<RegisterKind[]>("/registers/catalog"),
  listRegister: (pid: string, kind: string, q = "") =>
    api.get<RegisterItem[]>(`/projects/${pid}/registers/${kind}${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  getRegisterItem: (pid: string, kind: string, id: string) =>
    api.get<RegisterItemDetail>(`/projects/${pid}/registers/${kind}/${id}`),
  createRegisterItem: (
    pid: string,
    kind: string,
    body: { number?: string; title: string; status?: string; responsible?: string; dueDate?: string; data: Record<string, unknown> },
  ) => api.post<RegisterItemDetail>(`/projects/${pid}/registers/${kind}`, body),
  updateRegisterItem: (
    pid: string,
    kind: string,
    id: string,
    body: { title?: string; status?: string; responsible?: string; dueDate?: string; data?: Record<string, unknown> },
  ) => api.put<RegisterItem>(`/projects/${pid}/registers/${kind}/${id}`, body),
  /** Issue the NEXT revision. There is no edit — that is the point. */
  addRevision: (
    pid: string,
    kind: string,
    id: string,
    body: { status?: string; issuedOn?: string; data: Record<string, unknown> },
  ) => api.post<RegisterItemDetail>(`/projects/${pid}/registers/${kind}/${id}/revisions`, body),
  revisionFileUrl: (pid: string, kind: string, id: string, revId: string) =>
    `${BASE}/projects/${pid}/registers/${kind}/${id}/revisions/${revId}/file`,
  uploadRevisionFile: async (pid: string, kind: string, id: string, revId: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    const res = await fetch(`${BASE}/projects/${pid}/registers/${kind}/${id}/revisions/${revId}/file`, {
      method: "POST", credentials: "include", body,
    });
    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : null;
    if (!res.ok) {
      let detail = res.statusText;
      if (data && typeof data === "object" && "detail" in data) detail = String((data as { detail: unknown }).detail);
      throw new ApiError(res.status, detail, data);
    }
    return data as RegisterItemDetail;
  },
  listTransmittals: (pid: string) => api.get<Transmittal[]>(`/projects/${pid}/transmittals`),

  // dated site records — the daily log and meetings
  logsCatalog: () => api.get<LogKind[]>("/logs/catalog"),
  listLogs: (pid: string, kind: string) => api.get<LogEntry[]>(`/projects/${pid}/logs/${kind}`),
  createLog: (pid: string, kind: string, body: { entryDate: string; title?: string; data: Record<string, unknown> }) =>
    api.post<LogEntry>(`/projects/${pid}/logs/${kind}`, body),
  updateLog: (
    pid: string, kind: string, id: string,
    body: { title?: string; data?: Record<string, unknown>; amendmentNote?: string },
  ) => api.put<LogEntry>(`/projects/${pid}/logs/${kind}/${id}`, body),
  signOffLog: (pid: string, kind: string, id: string) =>
    api.post<LogEntry>(`/projects/${pid}/logs/${kind}/${id}/sign-off`),
  addAction: (pid: string, kind: string, id: string, body: { text: string; owner?: string; due?: string }) =>
    api.post<LogEntry>(`/projects/${pid}/logs/${kind}/${id}/actions`, body),
  updateAction: (
    pid: string, kind: string, id: string, actionId: string,
    body: { text?: string; owner?: string; due?: string; status?: string },
  ) => api.patch<LogEntry>(`/projects/${pid}/logs/${kind}/${id}/actions/${actionId}`, body),
  openActions: (pid: string) => api.get<OpenAction[]>(`/projects/${pid}/actions`),
  createTransmittal: (
    pid: string,
    body: { subject: string; toParty?: string; toName?: string; purpose?: string; notes?: string; itemIds: string[] },
  ) => api.post<Transmittal>(`/projects/${pid}/transmittals`, body),

  // project team (members) with per-project roles + abilities
  listMembers: (pid: string) => api.get<ProjectMember[]>(`/projects/${pid}/members`),
  addMember: (
    pid: string,
    body: {
      role?: string;
      abilities?: string[];
      groupId?: string | null;
      userId?: string;
      email?: string;
      name?: string;
      password?: string;
    },
  ) => api.post<ProjectMember>(`/projects/${pid}/members`, body),
  updateMember: (
    pid: string,
    mid: string,
    body: { role?: string; abilities?: string[]; groupId?: string | null },
  ) => api.patch<ProjectMember>(`/projects/${pid}/members/${mid}`, body),
  removeMember: (pid: string, mid: string) =>
    api.del<{ ok: boolean }>(`/projects/${pid}/members/${mid}`),

  // Test drive. Answers 404 unless the API has demo mode on, so every caller
  // treats a failure as "there is no test drive here" rather than an error.
  demoAccounts: (slug: string) =>
    api.get<DemoAccount[]>(`/demo/accounts?tenant=${encodeURIComponent(slug)}`),

  // project forms — the controlled-document workflow
  formsCatalog: () => api.get<FormType[]>("/forms/catalog"),
  myActions: () => api.get<MyAction[]>("/me/actions"),
  formsMeta: (pid: string) => api.get<FormsMeta>(`/projects/${pid}/forms/meta`),
  listForms: (pid: string, opts: { code?: string; openOnly?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.code) q.set("code", opts.code);
    if (opts.openOnly) q.set("open_only", "true");
    const qs = q.toString();
    return api.get<ProjectForm[]>(`/projects/${pid}/forms${qs ? `?${qs}` : ""}`);
  },
  raiseForm: (
    pid: string,
    body: { code: string; subject?: string; data: Record<string, unknown>; parentFormId?: string },
  ) => api.post<ProjectFormDetail>(`/projects/${pid}/forms`, body),
  getForm: (pid: string, fid: string) =>
    api.get<ProjectFormDetail>(`/projects/${pid}/forms/${fid}`),
  actOnForm: (
    pid: string,
    fid: string,
    body: {
      decision?: string;
      comment?: string;
      internal?: boolean;
      data?: Record<string, unknown>;
      resubmit?: boolean;
    },
  ) => api.post<ProjectFormDetail>(`/projects/${pid}/forms/${fid}/actions`, body),

  // form attachments (multipart — no JSON content-type)
  uploadAttachment: async (pid: string, fid: string, file: File, internal = false) => {
    const body = new FormData();
    body.append("file", file);
    body.append("internal", String(internal));
    const res = await fetch(`${BASE}/projects/${pid}/forms/${fid}/attachments`, {
      method: "POST",
      credentials: "include",
      body,
    });
    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : null;
    if (!res.ok) {
      let detail = res.statusText;
      if (data && typeof data === "object" && "detail" in data) {
        detail = String((data as { detail: unknown }).detail);
      }
      throw new ApiError(res.status, detail, data);
    }
    return data as ProjectFormDetail;
  },
  attachmentUrl: (pid: string, fid: string, aid: string) =>
    `${BASE}/projects/${pid}/forms/${fid}/attachments/${aid}`,
  // Same file, rendered in place. The API only honours this for bitmap images.
  photoUrl: (pid: string, fid: string, aid: string) =>
    `${BASE}/projects/${pid}/forms/${fid}/attachments/${aid}?inline=1`,
  deleteAttachment: (pid: string, fid: string, aid: string) =>
    api.del<ProjectFormDetail>(`/projects/${pid}/forms/${fid}/attachments/${aid}`),

  // view as — open the app as another user, without their password
  viewAsTargets: () => api.get<ViewAsTarget[]>("/admin/view-as/targets"),
  viewAsStart: (userId: string) => api.post<User>(`/admin/view-as/${userId}`),
  viewAsStop: () => api.post<User>("/admin/view-as"),

  // platform canvas (platform admin only)
  canvasOverview: () => api.get<CanvasOverview>("/admin/canvas/overview"),
  canvasArchitecture: () => api.get<Blueprint>("/admin/canvas/architecture"),
  canvasUsers: () => api.get<PlatformUsers>("/admin/canvas/users"),
  canvasReleases: () => api.get<ReleaseLog>("/admin/canvas/releases"),
  canvasReadiness: () => api.get<Readiness>("/admin/canvas/readiness"),
  canvasSeedDemoUsers: () => api.post<DemoUsersResult>("/admin/canvas/demo-users"),
  canvasPlatformSchema: () => api.get<CanvasSchema>("/admin/canvas/schema/platform"),
  canvasTenantSchema: (slug: string) =>
    api.get<CanvasSchema>(`/admin/canvas/schema/tenant/${slug}`),
  canvasProcesses: () =>
    api.get<{ processes: CanvasProcess[]; parties: Record<string, string> }>(
      "/admin/canvas/processes",
    ),
  canvasForms: () => api.get<{ forms: FormType[] }>("/admin/canvas/forms"),
  canvasQuery: (source: string, sql: string) =>
    api.post<QueryResult>("/admin/canvas/query", { source, sql }),

  // project groups (shared role + abilities for a set of members)
  listGroups: (pid: string) => api.get<ProjectGroup[]>(`/projects/${pid}/groups`),
  createGroup: (pid: string, body: { name: string; role: string; abilities?: string[] }) =>
    api.post<ProjectGroup>(`/projects/${pid}/groups`, body),
  updateGroup: (
    pid: string,
    gid: string,
    body: { name?: string; role?: string; abilities?: string[] },
  ) => api.patch<ProjectGroup>(`/projects/${pid}/groups/${gid}`, body),
  deleteGroup: (pid: string, gid: string) =>
    api.del<{ ok: boolean }>(`/projects/${pid}/groups/${gid}`),

  // tenant self-serve (tenant admins)
  getTenantProfile: () => api.get<TenantConfig>("/tenant/profile"),
  updateTenantProfile: (body: {
    name?: string;
    theme?: Record<string, unknown>;
    profile?: Record<string, unknown>;
  }) => api.put<TenantConfig>("/tenant/profile", body),

  // users (tenant admins + platform admin via ?tenant=)
  listUsers: (tenant?: string) =>
    api.get<UserListItem[]>(`/users${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""}`),
  createUser: (body: { email: string; name: string; password: string; roles: string[] }, tenant?: string) =>
    api.post<UserListItem>(`/users${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""}`, body),
  updateUser: (id: string, body: { name?: string; roles?: string[]; active?: boolean }) =>
    api.patch<UserListItem>(`/users/${id}`, body),
  deleteUser: (id: string) => api.del<{ ok: boolean }>(`/users/${id}`),

  // company vault (single source of truth)
  listFolders: () => api.get<Folder[]>("/vault/folders"),
  createFolder: (body: { parentId: string | null; name: string }) =>
    api.post<Folder>("/vault/folders", body),
  renameFolder: (id: string, name: string) => api.patch<Folder>(`/vault/folders/${id}`, { name }),
  deleteFolder: (id: string) => api.del<{ ok: boolean }>(`/vault/folders/${id}`),
  listVaultFiles: (folderId?: string) =>
    api.get<VaultFile[]>(`/vault/files${folderId ? `?folderId=${encodeURIComponent(folderId)}` : ""}`),
  uploadVaultFile: async (folderId: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    const res = await fetch(`${BASE}/vault/files?folderId=${encodeURIComponent(folderId)}`, {
      method: "POST",
      credentials: "include",
      body,
    });
    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : null;
    if (!res.ok) {
      let detail = res.statusText;
      if (data && typeof data === "object" && "detail" in data) {
        detail = String((data as { detail: unknown }).detail);
      }
      throw new ApiError(res.status, detail, data);
    }
    return data as VaultFile;
  },
  vaultFileUrl: (id: string) => `${BASE}/vault/files/${id}/download`,
  listGuides: (lang: string) => api.get<GuideIndexItem[]>(`/guide?lang=${lang}`),
  getGuide: (key: string, lang: string) =>
    api.get<BloxGuide>(`/guide/${encodeURIComponent(key)}?lang=${lang}`),
  deleteVaultFile: (id: string) => api.del<{ ok: boolean }>(`/vault/files/${id}`),

  // control plane (platform admin)
  listTenants: () => api.get<TenantSummary[]>("/admin/tenants"),
  setTenantModule: (slug: string, moduleKey: string, enabled: boolean) =>
    api.patch<TenantSummary>(`/admin/tenants/${slug}/modules`, { module: moduleKey, enabled }),
  setTenantStatus: (slug: string, status: "active" | "suspended") =>
    api.patch<TenantSummary>(`/admin/tenants/${slug}/status`, { status }),
  createTenant: (body: { slug: string; name: string; type: string }) =>
    api.post<TenantSummary>("/admin/tenants", body),
  updateTenant: (
    slug: string,
    body: {
      name?: string;
      type?: string;
      theme?: Record<string, unknown>;
      ai?: Record<string, unknown>;
      limits?: Record<string, unknown>;
    },
  ) => api.put<TenantSummary>(`/admin/tenants/${slug}`, body),
  duplicateTenant: (slug: string, body: { slug: string; name: string }) =>
    api.post<TenantSummary>(`/admin/tenants/${slug}/duplicate`, body),
};
